import type { EnvImportResult } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { WarningsList } from '@/components/WarningsList';
import { Tag, VisibilityBadge } from '@/components/badges';
import { lineCountLabel } from './envHelpers';
import { ACTION_TONE } from './importModel';

type Item = EnvImportResult['items'][number];

/** Dry-run result: the per-key actions and the document-level warnings. */
export function ImportPreview({ preview }: { preview: EnvImportResult }) {
  return (
    <div data-testid="env-import-preview" className="flex flex-col gap-2 rounded-lg border bg-secondary/40 p-3">
      <PreviewSummary summary={preview.summary} />
      <ul className="flex flex-col gap-1.5">
        {preview.items.map((item) => (
          <PreviewItem key={item.key} item={item} />
        ))}
      </ul>
      <WarningsList warnings={preview.warnings} compact />
    </div>
  );
}

function PreviewSummary({ summary }: { summary: EnvImportResult['summary'] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px]">
      <span className="font-semibold text-foreground">Preview</span>
      <Tag className="text-succeeded">{summary.created} create</Tag>
      <Tag className="text-running">{summary.newVersions} new version</Tag>
      <Tag>{summary.unchanged} unchanged</Tag>
      <Tag>{summary.skipped} skipped</Tag>
      <Tag className={summary.errors > 0 ? 'text-failed' : undefined}>{summary.errors} errors</Tag>
    </div>
  );
}

function PreviewItem({ item }: { item: Item }) {
  const lines = lineCountLabel(item.lineCount);
  return (
    <li data-testid={`env-import-item-${item.key}`} data-action={item.action} className="flex flex-col gap-1 rounded-md border bg-card px-2.5 py-1.5">
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
        <span className="font-medium text-foreground">{item.key}</span>
        <span className={cn('uppercase', ACTION_TONE[item.action])}>{item.action.replace('_', ' ')}</span>
        {item.visibility ? <VisibilityBadge visibility={item.visibility} /> : null}
        {item.valueType ? <Tag>{item.valueType}</Tag> : null}
        {lines ? <span className="text-subtle">{lines}</span> : null}
      </div>
      <WarningsList warnings={item.warnings} compact />
    </li>
  );
}
